import axios from "axios";
import { mixPanel } from "./mixpanel.config";

/**
 * Read the `exp` claim out of a stored JWT without pulling in a dependency.
 * Returns undefined for a malformed token or one with no expiry (legacy tokens
 * minted before AUTH_TOKEN_TTL was configured carry no `exp` at all).
 */
function getTokenExpirySeconds(token) {
  try {
    const payload = token.split(".")[1];
    if (!payload) return undefined;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = JSON.parse(json).exp;
    return typeof exp === "number" ? exp : undefined;
  } catch (e) {
    return undefined;
  }
}

/**
 * True only when we hold a session whose token has genuinely expired.
 *
 * This is deliberately narrow. A blanket "log out on any 401" is what this
 * interceptor used to do before it was disabled, and it logs people out
 * spuriously: plenty of 401s are not session death (an optional-auth endpoint
 * hit anonymously, a transient DB blip in the auth middleware, a 403-shaped
 * permission error). Checking the stored token's own `exp` locally means we
 * only act when the session is provably dead, so there is nothing to recover.
 */
function hasExpiredSession() {
  let stored;
  try {
    stored = JSON.parse(localStorage.getItem("pensil.user") || "null");
  } catch (e) {
    return false;
  }
  if (!stored?.token) return false;

  const exp = getTokenExpirySeconds(stored.token);
  if (exp === undefined) return false; // legacy token with no expiry — never force a logout

  return exp * 1000 <= Date.now();
}

export function configureAxios() {
  axios.interceptors.response.use(undefined, function (error) {
    // Session expiry (SCQP, 2026-09-16). V1 has no refresh-token flow, so an
    // expired token cannot be renewed silently — the only recovery is a fresh
    // login. Without this, an expired session just makes every request 401 and
    // the app renders an empty shell with no way out but clearing site data.
    // Matters now that admins get a short AUTH_TOKEN_TTL_ADMIN.
    if (error.response?.status === 401 && hasExpiredSession()) {
      localStorage.removeItem("pensil.user");
      mixPanel.reset();

      // Come back to where they were after re-authenticating; the login pages
      // already honour ?redirect= (same-origin relative paths only).
      const { pathname, search } = window.location;
      const target = pathname + search;
      const alreadyOnLogin = pathname.startsWith("/login");
      const redirect =
        !alreadyOnLogin && target && target !== "/"
          ? "?redirect=" + encodeURIComponent(target)
          : "";

      if (!alreadyOnLogin) {
        window.location.href = window.location.origin + "/login" + redirect;
      }
    }

    if (error.response?.status === 503) {
      alert("Service unavailable, please try again later");
    }

    return Promise.reject(error);
  });
}
